import React, { useState, useEffect, useRef } from 'react';
import ChangePassword from './ChangePassword';
import Footer from './Footer';
import API_URL from '../config/api';
import './AdminDashboard.css';

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddResellerModal, setShowAddResellerModal] = useState(false);
  const [showFundModal, setShowFundModal] = useState(false);
  const [showFundResellerModal, setShowFundResellerModal] = useState(false);
  const [showDeductResellerModal, setShowDeductResellerModal] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [activatePackage, setActivatePackage] = useState('3months');
  const [deductAmount, setDeductAmount] = useState('');
  const [editUser, setEditUser] = useState({ username: '', email: '' });
  const [editUserId, setEditUserId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [now, setNow] = useState(new Date());
  const USERS_PER_PAGE = 10;
  const menuRef = useRef(null);

  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', package_type: '3months' });
  const [newReseller, setNewReseller] = useState({ username: '', email: '', password: '', initial_wallet: 0 });
  const [fundAmount, setFundAmount] = useState('');
  const [fundResellerData, setFundResellerData] = useState({ reseller_username: '', amount: '' });

  const token = localStorage.getItem('token');

  const packages = {
    "12hours": { price: 1, label: "12 Hours - $1" },
    "3months": { price: 14, label: "3 Months - $14" },
    "6months": { price: 19, label: "6 Months - $19" },
    "1year": { price: 25, label: "1 Year - $25" }
  };

  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ message, onConfirm });
  };

  useEffect(() => { fetchData(); }, [activeTab]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, userFilter]);

  useEffect(() => {
    const handleClose = () => setOpenMenuId(null);
    document.addEventListener('click', handleClose);
    return () => document.removeEventListener('click', handleClose);
  }, []);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const profileRes = await fetch(`${API_URL}/api/user/profile`, { headers: { 'Authorization': `Bearer ${token}` } });
      const profileData = await profileRes.json();
      setProfile(profileData.user);

      if (activeTab === 'users') {
        const usersRes = await fetch(`${API_URL}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } });
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      } else if (activeTab === 'resellers') {
        const resellersRes = await fetch(`${API_URL}/api/resellers`, { headers: { 'Authorization': `Bearer ${token}` } });
        const resellersData = await resellersRes.json();
        setResellers(resellersData.resellers || []);
      }
    } catch (err) {
      showToast('Failed to fetch data', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const allFilteredUsers = users.filter(u => {
    if (u.role !== 'user') return false;
    if (userFilter === 'active' && !u.active) return false;
    if (userFilter === 'inactive' && u.active) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const addedBy = (u.added_by || 'Registered').toLowerCase();
    return u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || addedBy.includes(q);
  });

  const totalPages = Math.ceil(allFilteredUsers.length / USERS_PER_PAGE);
  const filteredUsers = allFilteredUsers.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  const calculateDaysLeft = (availableUntil) => {
    if (!availableUntil) return { text: 'N/A', color: '#999' };
    const expiry = new Date(availableUntil);
    const diffTime = expiry - now;

    if (diffTime <= 0) return { text: '⛔ Expired', color: '#e53935' };

    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const mins = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    const secs = Math.floor((diffTime % (1000 * 60)) / 1000);

    let text;
    if (days > 0) text = `${days}d ${hours}h ${mins}m ${secs}s`;
    else if (hours > 0) text = `${hours}h ${mins}m ${secs}s`;
    else if (mins > 0) text = `${mins}m ${secs}s`;
    else text = `${secs}s`;

    let color;
    if (diffTime < 3600000) color = '#e53935';        // under 1 hour — red
    else if (diffTime < 86400000) color = '#ff9800';  // under 1 day — orange
    else if (diffTime < 259200000) color = '#f39c12'; // under 3 days — yellow
    else color = '#2e7d32';                            // healthy — green

    return { text, color };
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/users/add`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(newUser) });
      const data = await res.json();
      if (res.ok) { showToast(`User created! Deducted $${data.deducted}. Balance: $${data.remaining_balance}`); setShowAddUserModal(false); setNewUser({ username: '', email: '', password: '', package_type: '3months' }); fetchData(true); }
      else showToast(data.message, 'error');
    } catch { showToast('Failed to add user', 'error'); }
  };

  const handleAddReseller = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/resellers/add`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(newReseller) });
      const data = await res.json();
      if (res.ok) { showToast('Reseller created successfully!'); setShowAddResellerModal(false); setNewReseller({ username: '', email: '', password: '', initial_wallet: 0 }); fetchData(true); }
      else showToast(data.message, 'error');
    } catch { showToast('Failed to add reseller', 'error'); }
  };

  const handleFundSelf = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/wallet/fund-self`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ amount: parseFloat(fundAmount) }) });
      const data = await res.json();
      if (res.ok) { showToast(`Added $${fundAmount}! Balance: $${data.new_balance}`); setShowFundModal(false); setFundAmount(''); fetchData(true); }
      else showToast(data.message, 'error');
    } catch { showToast('Failed to fund wallet', 'error'); }
  };

  const handleFundReseller = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/wallet/fund-reseller`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(fundResellerData) });
      const data = await res.json();
      if (res.ok) { showToast(`Transferred $${fundResellerData.amount} to ${fundResellerData.reseller_username}`); setShowFundResellerModal(false); setFundResellerData({ reseller_username: '', amount: '' }); fetchData(true); }
      else showToast(data.message, 'error');
    } catch { showToast('Failed to fund reseller', 'error'); }
  };

  const handleDeductReseller = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/wallet/deduct-reseller`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reseller_username: selectedReseller.username, amount: parseFloat(deductAmount) })
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`Deducted $${deductAmount} from ${selectedReseller.username}. New balance: $${data.new_balance}`);
        setShowDeductResellerModal(false); setDeductAmount(''); setSelectedReseller(null); fetchData(true);
      } else showToast(data.message, 'error');
    } catch { showToast('Failed to deduct from reseller', 'error'); }
  };

  const deleteReseller = async (resellerId) => {
    showConfirm('Are you sure you want to delete this reseller?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/${resellerId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok) { showToast('Reseller deleted successfully'); fetchData(true); }
        else showToast(data.message, 'error');
      } catch { showToast('Failed to delete reseller', 'error'); }
    });
  };

  const toggleUserActive = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/toggle-active`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) { showToast(data.message); fetchData(true); }
      else showToast(data.message, 'error');
    } catch { showToast('Failed to toggle user status', 'error'); }
  };

  const handleActivateUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users/${selectedUser._id}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_type: activatePackage })
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`✅ ${selectedUser.username} activated with ${packages[activatePackage].label}. Deducted $${data.deducted}. Balance: $${data.remaining_balance}`);
        setShowActivateModal(false); setSelectedUser(null); fetchData(true);
      } else showToast(data.message, 'error');
    } catch { showToast('Failed to activate user', 'error'); }
  };

  const openActivateModal = (user) => {
    if (user.active) return;
    setSelectedUser(user); setActivatePackage('3months'); setShowActivateModal(true);
  };

  const deleteUser = async (userId) => {
    showConfirm('Are you sure you want to delete this user?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/${userId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok) { showToast('User deleted successfully'); fetchData(true); }
        else showToast(data.message, 'error');
      } catch { showToast('Failed to delete user', 'error'); }
    });
  };

  const resetMachineBind = async (userId) => {
    showConfirm('Reset machine binding for this user?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/${userId}/reset-machine`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok) { showToast('✅ Machine binding reset'); fetchData(true); }
        else showToast(data.message, 'error');
      } catch { showToast('Failed to reset machine binding', 'error'); }
    });
  };

  const resetUserPassword = async (userId) => {
    showConfirm('Reset password to default (12345t)?', async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/${userId}/reset-password`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok) { showToast('✅ Password reset to 12345t'); fetchData(true); }
        else showToast(data.message, 'error');
      } catch { showToast('Failed to reset password', 'error'); }
    });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/users/${editUserId}/update`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editUser)
      });
      const data = await res.json();
      if (res.ok) { showToast('✅ User updated'); setShowEditModal(false); fetchData(true); }
      else showToast(data.message, 'error');
    } catch { showToast('Failed to update user', 'error'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const closeMenu = (fn) => { fn(); setMobileMenuOpen(false); };

  const UserActionMenu = ({ user }) => {
    const btnRef = useRef(null);
    const [openUp, setOpenUp] = useState(false);

    const handleOpen = (e) => {
      e.stopPropagation();
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setOpenUp(spaceBelow < 260);
      }
      setOpenMenuId(openMenuId === user._id ? null : user._id);
    };

    return (
      <div className="ad-action-menu-wrap" onClick={e => e.stopPropagation()}>
        <button ref={btnRef} className="ad-action-menu-btn" onClick={handleOpen}>⋮</button>
        {openMenuId === user._id && (
          <div className={`ad-action-dropdown ${openUp ? 'open-up' : ''}`}>
            <button onClick={() => { toggleUserActive(user._id); setOpenMenuId(null); }}>
              {user.active ? '🔴 Disable' : '🟢 Enable'}
            </button>
            <button onClick={() => { openActivateModal(user); setOpenMenuId(null); }} disabled={user.active} style={{ opacity: user.active ? 0.4 : 1 }}>
              ⚡ Activate
            </button>
            <button onClick={() => { setEditUserId(user._id); setEditUser({ username: user.username, email: user.email || '' }); setShowEditModal(true); setOpenMenuId(null); }}>
              ✏️ Edit
            </button>
            <button onClick={() => { resetMachineBind(user._id); setOpenMenuId(null); }}>
              🔗 Reset Bind
            </button>
            <button onClick={() => { resetUserPassword(user._id); setOpenMenuId(null); }}>
              🔑 Reset Password
            </button>
            <button className="danger" onClick={() => { deleteUser(user._id); setOpenMenuId(null); }}>
              🗑️ Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="ad-loading">Loading...</div>;

  return (
    <div className="ad-root">

      {/* HEADER */}
      <header className="ad-header">
        <div className="ad-header-brand">
          <span className="ad-brand-icon">🐯</span>
          <span className="ad-brand-name">Tiger Guard</span>
        </div>
        <div className="ad-header-desktop">
          <div className="ad-pill purple">
            <span>💰</span>
            <strong>${profile?.wallet || 0}</strong>
            <span className="ad-pill-label">Balance</span>
          </div>
          <div className="ad-pill purple">
            <span>💸</span>
            <strong>${profile?.total_spent || 0}</strong>
            <span className="ad-pill-label">Spent</span>
          </div>
          <span className="ad-username">{profile?.username}</span>
          <span className="ad-role-badge">Admin</span>
          <button className="ad-btn-fund" onClick={() => setShowFundModal(true)}>💵 Fund</button>
          <button className="ad-btn-icon" onClick={() => setShowChangePassword(true)} title="Change Password">🔐</button>
          <button className="ad-btn-logout" onClick={handleLogout}>Logout</button>
        </div>
        <button className={`ad-hamburger ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(v => !v)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </header>

      {/* MOBILE DRAWER */}
      <div className={`ad-mobile-drawer ${mobileMenuOpen ? 'visible' : ''}`} ref={menuRef}>
        <div className="ad-drawer-profile">
          <div className="ad-drawer-avatar">🐯</div>
          <div>
            <div className="ad-drawer-username">{profile?.username}</div>
            <span className="ad-role-badge" style={{ marginTop: 4 }}>Admin</span>
          </div>
        </div>
        <div className="ad-drawer-stats">
          <div className="ad-drawer-stat">
            <span className="ad-drawer-stat-icon">💰</span>
            <div><div className="ad-drawer-stat-value">${profile?.wallet || 0}</div><div className="ad-drawer-stat-label">Balance</div></div>
          </div>
          <div className="ad-drawer-stat">
            <span className="ad-drawer-stat-icon">💸</span>
            <div><div className="ad-drawer-stat-value">${profile?.total_spent || 0}</div><div className="ad-drawer-stat-label">Spent</div></div>
          </div>
          <div className="ad-drawer-stat">
            <span className="ad-drawer-stat-icon">👥</span>
            <div><div className="ad-drawer-stat-value">{users.filter(u => u.role === 'user').length}</div><div className="ad-drawer-stat-label">Users</div></div>
          </div>
        </div>
        <div className="ad-drawer-actions">
          <button className="ad-drawer-btn" onClick={() => closeMenu(() => setShowFundModal(true))}>💵 Fund My Wallet</button>
          <button className="ad-drawer-btn" onClick={() => closeMenu(() => setShowFundResellerModal(true))}>💸 Fund Reseller</button>
          <button className="ad-drawer-btn secondary" onClick={() => closeMenu(() => setShowChangePassword(true))}>🔐 Change Password</button>
          <button className="ad-drawer-btn danger" onClick={handleLogout}>🚪 Logout</button>
        </div>
      </div>

      {mobileMenuOpen && <div className="ad-backdrop" onClick={() => setMobileMenuOpen(false)} />}

      {/* MAIN */}
      <main className="ad-main">
        <div className="ad-tabs">
          <button className={`ad-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => { setActiveTab('users'); setUserFilter('all'); }}>
            👥 Users
            <span className={`ad-tab-count ${userFilter === 'all' && activeTab === 'users' ? 'selected' : ''}`} onClick={e => { e.stopPropagation(); setActiveTab('users'); setUserFilter('all'); }}>
              {users.filter(u => u.role === 'user').length}
            </span>
            <span className={`ad-tab-active ${userFilter === 'active' ? 'filter-selected' : ''}`} onClick={e => { e.stopPropagation(); setActiveTab('users'); setUserFilter(userFilter === 'active' ? 'all' : 'active'); }} style={{ cursor: 'pointer' }}>
              ✓ {users.filter(u => u.role === 'user' && u.active).length}
            </span>
            <span className={`ad-tab-inactive ${userFilter === 'inactive' ? 'filter-selected' : ''}`} onClick={e => { e.stopPropagation(); setActiveTab('users'); setUserFilter(userFilter === 'inactive' ? 'all' : 'inactive'); }} style={{ cursor: 'pointer' }}>
              ✗ {users.filter(u => u.role === 'user' && !u.active).length}
            </span>
          </button>
          <button className={`ad-tab ${activeTab === 'resellers' ? 'active' : ''}`} onClick={() => setActiveTab('resellers')}>
            🏪 Resellers <span className="ad-tab-count">{resellers.length}</span>
          </button>
        </div>

        <div className="ad-tab-panel">

          {/* USERS TAB */}
          {activeTab === 'users' && (
            <>
              <div className="ad-panel-header">
                <h2>User Management</h2>
                <button className="ad-btn-primary" onClick={() => setShowAddUserModal(true)}>➕ Add User</button>
              </div>

              <div className="ad-search-wrap">
                <input type="text" className="ad-search" placeholder="🔍 Search by username, email or added by..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                {searchQuery && <button className="ad-search-clear" onClick={() => setSearchQuery('')}>✕</button>}
              </div>

              {userFilter !== 'all' && (
                <div className="ad-filter-indicator">
                  Showing: <strong>{userFilter === 'active' ? '✓ Active' : '✗ Inactive'} users</strong>
                  <button className="ad-filter-clear" onClick={() => setUserFilter('all')}>✕ Clear filter</button>
                </div>
              )}

              {/* Desktop Table */}
              <div className="ad-table-wrap">
                <table className="ad-table">
                  <thead>
                    <tr><th>Username</th><th>Email</th><th>Created</th><th>Expiry</th><th>Package</th><th>Time Left</th><th>Wallet</th><th>Added By</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr><td colSpan="9" className="ad-empty-row">{searchQuery ? `No users matching "${searchQuery}"` : 'No users found'}</td></tr>
                    ) : filteredUsers.map(user => {
                      const countdown = calculateDaysLeft(user.available_until);
                      return (
                        <tr key={user._id}>
                          <td>{user.username}</td>
                          <td>{user.email || 'N/A'}</td>
                          <td>{new Date(user.created_at).toLocaleDateString()}{' '}{new Date(user.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                          <td>{user.available_until ? new Date(user.available_until).toLocaleDateString() + ' ' + new Date(user.available_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}</td>
                          <td>{user.package_type || 'N/A'}</td>
                          <td>
                            <span style={{ color: countdown.color, fontWeight: 600, fontFamily: 'monospace', fontSize: '13px' }}>
                              {countdown.text}
                            </span>
                          </td>
                          <td>${user.wallet}</td>
                          <td>{user.added_by || 'Registered'}</td>
                          <td><span className={`ad-status ${user.active ? 'active' : 'inactive'}`}>{user.active ? '✓ Active' : '✗ Inactive'}</span></td>
                          <td><UserActionMenu user={user} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="ad-mobile-cards">
                {filteredUsers.length === 0 ? (
                  <div className="ad-empty-card">{searchQuery ? `No users matching "${searchQuery}"` : 'No users found'}</div>
                ) : filteredUsers.map(user => {
                  const countdown = calculateDaysLeft(user.available_until);
                  return (
                    <div className="ad-user-card" key={user._id}>
                      <div className="ad-user-card-header">
                        <div className="ad-user-card-left">
                          <div className="ad-user-card-name">{user.username}</div>
                          <div className="ad-user-card-email">{user.email || 'No email'}</div>
                        </div>
                        <div className="ad-user-card-right">
                          <span className={`ad-status ${user.active ? 'active' : 'inactive'}`}>{user.active ? 'Active' : 'Inactive'}</span>
                          <label className="ad-toggle-switch">
                            <input type="checkbox" checked={user.active} onChange={() => toggleUserActive(user._id)} />
                            <span className="ad-toggle-slider" />
                          </label>
                        </div>
                      </div>
                      <div className="ad-user-card-body">
                          <div className="ad-user-card-field">
                          <div className="ad-user-card-field-label">Created</div>
                          <div className="ad-user-card-field-value">
                          {new Date(user.created_at).toLocaleString("en-US", {
                            timeZone: "Africa/Nairobi",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </div>
  
                        </div>
                        <div className="ad-user-card-field">
                          <div className="ad-user-card-field-label">Package</div>
                          <div className="ad-user-card-field-value">{user.package_type || 'N/A'}</div>
                        </div>
                        <div className="ad-user-card-field">
                          <div className="ad-user-card-field">
                          <div className="ad-user-card-field-label">Expiry</div>
                          <div className="ad-user-card-field-value">{new Date(user.available_until).toLocaleString("en-US", {
                            timeZone: "Africa/Nairobi",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}</div>
                        </div>
                          <div className="ad-user-card-field-value">
                            
                          </div>
                        </div>
                        <div className="ad-user-card-field">
                          <div className="ad-user-card-field-label">Time Left</div>
                          <div className="ad-user-card-field-value" style={{ color: countdown.color, fontWeight: 600, fontFamily: 'monospace' }}>
                            {countdown.text}
                          </div>
                          
                        </div>
                        <div className="ad-user-card-field">
                          <div className="ad-user-card-field-label">Added By</div>
                          <div className="ad-user-card-field-value">{user.added_by || 'Registered'}</div>
                        </div>
                          <div className="ad-user-card-field">
                          <div className="ad-user-card-field-label">Cost</div>
                          <div className="ad-user-card-field-value">{packages[user.package_type]?.price ? `$${packages[user.package_type].price}` : "N/A"}</div>
                        </div>

                      </div>
                      <div className="ad-user-card-actions">
                        <div className="ad-action-menu-wrap" style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
                          <button className="ad-action-menu-btn full" onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === user._id ? null : user._id); }}>⋮ Actions</button>
                          {openMenuId === user._id && (
                            <div className="ad-action-dropdown">
                              <button onClick={() => { toggleUserActive(user._id); setOpenMenuId(null); }}>{user.active ? '🔴 Disable' : '🟢 Enable'}</button>
                              <button onClick={() => { openActivateModal(user); setOpenMenuId(null); }} disabled={user.active} style={{ opacity: user.active ? 0.4 : 1 }}>⚡ Activate</button>
                              <button onClick={() => { setEditUserId(user._id); setEditUser({ username: user.username, email: user.email || '' }); setShowEditModal(true); setOpenMenuId(null); }}>✏️ Edit</button>
                              <button onClick={() => { resetMachineBind(user._id); setOpenMenuId(null); }}>🔗 Reset Bind</button>
                              <button onClick={() => { resetUserPassword(user._id); setOpenMenuId(null); }}>🔑 Reset Password</button>
                              <button className="danger" onClick={() => { deleteUser(user._id); setOpenMenuId(null); }}>🗑️ Delete</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="ad-pagination">
                  <button className="ad-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button key={page} className={`ad-page-btn ${currentPage === page ? 'active' : ''}`} onClick={() => setCurrentPage(page)}>{page}</button>
                  ))}
                  <button className="ad-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
                  <span className="ad-page-info">Page {currentPage} of {totalPages} &nbsp;·&nbsp; {allFilteredUsers.length} users</span>
                </div>
              )}
            </>
          )}

          {/* RESELLERS TAB */}
          {activeTab === 'resellers' && (
            <>
              <div className="ad-panel-header">
                <h2>Reseller Management</h2>
                <div className="ad-panel-header-actions">
                  <button className="ad-btn-secondary" onClick={() => setShowFundResellerModal(true)}>💸 Fund Reseller</button>
                  <button className="ad-btn-primary" onClick={() => setShowAddResellerModal(true)}>➕ Add Reseller</button>
                </div>
              </div>
              <div className="ad-table-wrap">
                <table className="ad-table">
                  <thead>
                    <tr><th>Username</th><th>Email</th><th>Wallet</th><th>Total Spent</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {resellers.length === 0 ? (
                      <tr><td colSpan="6" className="ad-empty-row">No resellers found</td></tr>
                    ) : resellers.map(r => (
                      <tr key={r._id}>
                        <td>{r.username}</td>
                        <td>{r.email || 'N/A'}</td>
                        <td>${r.wallet}</td>
                        <td>${r.total_spent}</td>
                        <td><span className={`ad-status ${r.active ? 'active' : 'inactive'}`}>{r.active ? '✓ Active' : '✗ Inactive'}</span></td>
                        <td>
                          <button onClick={() => toggleUserActive(r._id)} className="ad-btn-small toggle">{r.active ? 'Disable' : 'Enable'}</button>
                          <button onClick={() => { setSelectedReseller(r); setDeductAmount(''); setShowDeductResellerModal(true); }} className="ad-btn-small deduct">Deduct</button>
                          <button onClick={() => deleteReseller(r._id)} className="ad-btn-small delete">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="ad-mobile-cards">
                {resellers.length === 0 ? (
                  <div className="ad-empty-card">No resellers found</div>
                ) : resellers.map(r => (
                  <div className="ad-user-card" key={r._id}>
                    <div className="ad-user-card-header">
                      <div className="ad-user-card-left">
                        <div className="ad-user-card-name">{r.username}</div>
                        <div className="ad-user-card-email">{r.email || 'No email'}</div>
                      </div>
                      <div className="ad-user-card-right">
                        <span className={`ad-status ${r.active ? 'active' : 'inactive'}`}>{r.active ? 'Active' : 'Inactive'}</span>
                        <label className="ad-toggle-switch">
                          <input type="checkbox" checked={r.active} onChange={() => toggleUserActive(r._id)} />
                          <span className="ad-toggle-slider" />
                        </label>
                      </div>
                    </div>
                    <div className="ad-user-card-body">
                      <div className="ad-user-card-field"><div className="ad-user-card-field-label">Wallet</div><div className="ad-user-card-field-value">${r.wallet}</div></div>
                      <div className="ad-user-card-field"><div className="ad-user-card-field-label">Total Spent</div><div className="ad-user-card-field-value">${r.total_spent}</div></div>
                    </div>
                    <div className="ad-user-card-actions">
                      <button className="ad-btn-small deduct" onClick={() => { setSelectedReseller(r); setDeductAmount(''); setShowDeductResellerModal(true); }}>💸 Deduct</button>
                      <button className="ad-btn-small delete" onClick={() => deleteReseller(r._id)}>🗑️ Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* ACTIVATE USER MODAL */}
      {showActivateModal && selectedUser && (
        <div className="ad-overlay" onClick={() => setShowActivateModal(false)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-header">
              <h3>⚡ Activate User</h3>
              <button className="ad-modal-close" onClick={() => setShowActivateModal(false)}>✕</button>
            </div>
            <div style={{ padding: '18px 22px 22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="ad-activate-user-info">
                <span className="ad-activate-avatar">👤</span>
                <div>
                  <div className="ad-activate-name">{selectedUser.username}</div>
                  <div className="ad-activate-email">{selectedUser.email || 'No email'}</div>
                  <span className="ad-status inactive" style={{ marginTop: 4, display: 'inline-block' }}>✗ Currently Inactive</span>
                </div>
              </div>
              <label className="ad-field-label">Select Package</label>
              <div className="ad-package-grid">
                {Object.entries(packages).map(([key, pkg]) => (
                  <div key={key} className={`ad-package-option ${activatePackage === key ? 'selected' : ''}`} onClick={() => setActivatePackage(key)}>
                    <div className="ad-package-label">{pkg.label.split(' - ')[0]}</div>
                    <div className="ad-package-price">${pkg.price}</div>
                  </div>
                ))}
              </div>
              <p className="ad-hint">
                Cost: <strong>${packages[activatePackage]?.price}</strong> &nbsp;|&nbsp;
                Balance: <strong>${profile?.wallet || 0}</strong> &nbsp;|&nbsp;
                After: <strong>${Math.max(0, (profile?.wallet || 0) - packages[activatePackage]?.price)}</strong>
              </p>
              {(profile?.wallet || 0) < packages[activatePackage]?.price && (
                <div className="ad-alert error" style={{ margin: 0 }}>⚠️ Insufficient balance.</div>
              )}
              <div className="ad-modal-actions">
                <button type="button" onClick={() => setShowActivateModal(false)}>Cancel</button>
                <button className="ad-btn-primary" onClick={handleActivateUser} disabled={(profile?.wallet || 0) < packages[activatePackage]?.price}>
                  ⚡ Activate - ${packages[activatePackage]?.price}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {showEditModal && (
        <div className="ad-overlay" onClick={() => setShowEditModal(false)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-header">
              <h3>✏️ Edit User</h3>
              <button className="ad-modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <form onSubmit={handleEditUser}>
              <input type="text" placeholder="Username" value={editUser.username} onChange={e => setEditUser({...editUser, username: e.target.value})} required />
              <input type="email" placeholder="Email (optional)" value={editUser.email} onChange={e => setEditUser({...editUser, email: e.target.value})} />
              <div className="ad-modal-actions">
                <button type="button" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="ad-btn-primary">💾 Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DEDUCT RESELLER MODAL */}
      {showDeductResellerModal && selectedReseller && (
        <div className="ad-overlay" onClick={() => setShowDeductResellerModal(false)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-header">
              <h3>💸 Deduct from Reseller</h3>
              <button className="ad-modal-close" onClick={() => setShowDeductResellerModal(false)}>✕</button>
            </div>
            <form onSubmit={handleDeductReseller}>
              <div style={{ padding: '0 22px' }}>
                <div className="ad-activate-user-info" style={{ marginBottom: 12 }}>
                  <span className="ad-activate-avatar">🏪</span>
                  <div>
                    <div className="ad-activate-name">{selectedReseller.username}</div>
                    <div className="ad-activate-email">{selectedReseller.email || 'No email'}</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#667' }}>Current wallet: <strong>${selectedReseller.wallet}</strong></div>
                  </div>
                </div>
              </div>
              <div style={{ padding: '0 22px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input type="number" step="0.01" min="0.01" max={selectedReseller.wallet} placeholder="Amount to deduct ($)" value={deductAmount} onChange={e => setDeductAmount(e.target.value)} required />
                <p className="ad-hint">
                  Reseller balance: <strong>${selectedReseller.wallet}</strong> &nbsp;|&nbsp;
                  After: <strong>${Math.max(0, selectedReseller.wallet - (parseFloat(deductAmount) || 0)).toFixed(2)}</strong>
                </p>
                <div className="ad-modal-actions">
                  <button type="button" onClick={() => setShowDeductResellerModal(false)}>Cancel</button>
                  <button type="submit" className="ad-btn-deduct">💸 Deduct</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD USER MODAL */}
      {showAddUserModal && (
        <div className="ad-overlay" onClick={() => setShowAddUserModal(false)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-header">
              <h3>Add New User</h3>
              <button className="ad-modal-close" onClick={() => setShowAddUserModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddUser}>
              <input type="text" placeholder="Username" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} required />
              <input type="email" placeholder="Email (optional)" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} />
              <input type="password" placeholder="Password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required />
              <select value={newUser.package_type} onChange={e => setNewUser({...newUser, package_type: e.target.value})} required>
                {Object.entries(packages).map(([key, pkg]) => <option key={key} value={key}>{pkg.label}</option>)}
              </select>
              <div className="ad-modal-actions">
                <button type="button" onClick={() => setShowAddUserModal(false)}>Cancel</button>
                <button type="submit" className="ad-btn-primary">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD RESELLER MODAL */}
      {showAddResellerModal && (
        <div className="ad-overlay" onClick={() => setShowAddResellerModal(false)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-header">
              <h3>Add New Reseller</h3>
              <button className="ad-modal-close" onClick={() => setShowAddResellerModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddReseller}>
              <input type="text" placeholder="Username" value={newReseller.username} onChange={e => setNewReseller({...newReseller, username: e.target.value})} required />
              <input type="email" placeholder="Email (optional)" value={newReseller.email} onChange={e => setNewReseller({...newReseller, email: e.target.value})} />
              <input type="password" placeholder="Password" value={newReseller.password} onChange={e => setNewReseller({...newReseller, password: e.target.value})} required />
              <input type="number" placeholder="Initial Wallet Balance ($)" value={newReseller.initial_wallet} onChange={e => setNewReseller({...newReseller, initial_wallet: parseFloat(e.target.value)})} />
              <div className="ad-modal-actions">
                <button type="button" onClick={() => setShowAddResellerModal(false)}>Cancel</button>
                <button type="submit" className="ad-btn-primary">Create Reseller</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FUND SELF MODAL */}
      {showFundModal && (
        <div className="ad-overlay" onClick={() => setShowFundModal(false)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-header">
              <h3>Fund My Wallet</h3>
              <button className="ad-modal-close" onClick={() => setShowFundModal(false)}>✕</button>
            </div>
            <form onSubmit={handleFundSelf}>
              <input type="number" step="0.01" placeholder="Amount ($)" value={fundAmount} onChange={e => setFundAmount(e.target.value)} required />
              <p className="ad-hint">Current balance: <strong>${profile?.wallet || 0}</strong></p>
              <div className="ad-modal-actions">
                <button type="button" onClick={() => setShowFundModal(false)}>Cancel</button>
                <button type="submit" className="ad-btn-primary">Add Funds</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FUND RESELLER MODAL */}
      {showFundResellerModal && (
        <div className="ad-overlay" onClick={() => setShowFundResellerModal(false)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-header">
              <h3>Fund Reseller Wallet</h3>
              <button className="ad-modal-close" onClick={() => setShowFundResellerModal(false)}>✕</button>
            </div>
            <form onSubmit={handleFundReseller}>
              <select value={fundResellerData.reseller_username} onChange={e => setFundResellerData({...fundResellerData, reseller_username: e.target.value})} required>
                <option value="">Select Reseller</option>
                {resellers.map(r => <option key={r._id} value={r.username}>{r.username} (${r.wallet})</option>)}
              </select>
              <input type="number" step="0.01" placeholder="Amount ($)" value={fundResellerData.amount} onChange={e => setFundResellerData({...fundResellerData, amount: e.target.value})} required />
              <p className="ad-hint">Your balance: <strong>${profile?.wallet || 0}</strong></p>
              <div className="ad-modal-actions">
                <button type="button" onClick={() => setShowFundResellerModal(false)}>Cancel</button>
                <button type="submit" className="ad-btn-primary">Transfer Funds</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ChangePassword isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} onSuccess={msg => showToast(msg)} />
      <Footer variant="admin" />

      {/* CONFIRM DIALOG */}
      {confirmDialog && (
        <div className="ad-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="ad-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="ad-confirm-icon">⚠️</div>
            <div className="ad-confirm-message">{confirmDialog.message}</div>
            <div className="ad-confirm-actions">
              <button className="ad-confirm-cancel" onClick={() => setConfirmDialog(null)}>Cancel</button>
              <button className="ad-confirm-ok" onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST NOTIFICATIONS */}
      <div className="ad-toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`ad-toast ${toast.type}`}>
            <span>{toast.type === 'success' ? '✅' : '❌'}</span>
            <span>{toast.message}</span>
            <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}>✕</button>
          </div>
        ))}
      </div>

    </div>
  );
}